import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase-client";

interface LogErrorParams {
  context: string;
  error: string | Error;
  stackTrace?: string;
  additionalData?: Record<string, unknown>;
}

interface LogWarningParams {
  context: string;
  message: string;
  additionalData?: Record<string, unknown>;
}

interface LogInfoParams {
  context: string;
  message: string;
  additionalData?: Record<string, unknown>;
}

/**
 * Service for logging errors and warnings to Firestore for remote debugging
 * Works across web SPA, iOS, Android, and Firebase Functions
 */
class ErrorLoggingService {
  private readonly collectionName = "errorLogs";

  /**
   * Log an error to Firestore for remote debugging
   */
  async logError({ context, error, stackTrace, additionalData }: LogErrorParams): Promise<void> {
    try {
      const user = auth.currentUser;
      const deviceInfo = this.getDeviceInfo();

      await addDoc(collection(db, this.collectionName), {
        context,
        error: error.toString(),
        stackTrace: stackTrace?.toString() || null,
        userId: user?.uid || null,
        userEmail: user?.email || null,
        timestamp: serverTimestamp(),
        platform: deviceInfo.platform,
        platformVersion: deviceInfo.platformVersion,
        userAgent: deviceInfo.userAgent,
        isWeb: true,
        additionalData: additionalData || null,
        type: 'error',
      });

      console.error(`[ErrorLoggingService] Error logged to Firestore: ${context} - ${error}`);
    } catch (e) {
      // If we can't log to Firestore, at least log locally
      console.error('[ErrorLoggingService] Failed to log error to Firestore:', e);
    }
  }

  /**
   * Log a warning to Firestore for remote debugging
   */
  async logWarning({ context, message, additionalData }: LogWarningParams): Promise<void> {
    try {
      const user = auth.currentUser;
      const deviceInfo = this.getDeviceInfo();

      await addDoc(collection(db, this.collectionName), {
        context,
        message,
        userId: user?.uid || null,
        userEmail: user?.email || null,
        timestamp: serverTimestamp(),
        platform: deviceInfo.platform,
        platformVersion: deviceInfo.platformVersion,
        userAgent: deviceInfo.userAgent,
        isWeb: true,
        additionalData: additionalData || null,
        type: 'warning',
      });

      console.warn(`[ErrorLoggingService] Warning logged to Firestore: ${context} - ${message}`);
    } catch (e) {
      console.error('[ErrorLoggingService] Failed to log warning to Firestore:', e);
    }
  }

  /**
   * Log info message to Firestore for debugging
   */
  async logInfo({ context, message, additionalData }: LogInfoParams): Promise<void> {
    try {
      const user = auth.currentUser;
      const deviceInfo = this.getDeviceInfo();

      await addDoc(collection(db, this.collectionName), {
        context,
        message,
        userId: user?.uid || null,
        userEmail: user?.email || null,
        timestamp: serverTimestamp(),
        platform: deviceInfo.platform,
        platformVersion: deviceInfo.platformVersion,
        userAgent: deviceInfo.userAgent,
        isWeb: true,
        additionalData: additionalData || null,
        type: 'info',
      });

      console.info(`[ErrorLoggingService] Info logged to Firestore: ${context} - ${message}`);
    } catch (e) {
      console.error('[ErrorLoggingService] Failed to log info to Firestore:', e);
    }
  }

  private getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let platform = 'web';
    let platformVersion = 'unknown';

    // Detect OS
    if (userAgent.includes('Windows')) {
      platform = 'web-windows';
      const match = userAgent.match(/Windows NT (\d+\.\d+)/);
      platformVersion = match ? `Windows ${match[1]}` : 'Windows';
    } else if (userAgent.includes('Mac')) {
      platform = 'web-mac';
      const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]\d+)/);
      platformVersion = match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS';
    } else if (userAgent.includes('Linux')) {
      platform = 'web-linux';
      platformVersion = 'Linux';
    } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      platform = 'web-ios';
      const match = userAgent.match(/OS (\d+_\d+)/);
      platformVersion = match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
    } else if (userAgent.includes('Android')) {
      platform = 'web-android';
      const match = userAgent.match(/Android (\d+(\.\d+)?)/);
      platformVersion = match ? `Android ${match[1]}` : 'Android';
    }

    // Detect browser
    let browser = 'unknown';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      browser = 'Chrome';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browser = 'Safari';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
    } else if (userAgent.includes('Edg')) {
      browser = 'Edge';
    }

    return {
      platform,
      platformVersion,
      userAgent,
      browser,
    };
  }
}

// Export singleton instance
export const errorLoggingService = new ErrorLoggingService();
